import type { CalculationTree, DataOrigin, DataOriginQuestion, KpiResult } from '@netpurpose/types'
import type {
  CalculationTree as BackendCalculationTree,
  DataOrigin as BackendDataOrigin,
  OriginQuestion as BackendDataOriginQuestion,
  SdiFormulaCalculationTree,
} from '../../generated/facts'
import { parseSegment } from '../segment'

export type BackendKpiResult = {
  kpi_result_id: string
  value: [number | null, string]
  kpi_id: string
  entity_id: number
  start: string | null
  end: string
  year: number
  calculation_tree: BackendCalculationTree | null
}

// The backend sends the full segment where it has one to hand, and otherwise
// just its id, which is passed through for the caller to resolve or ignore.
const parseOriginSegment = (
  segment: BackendDataOriginQuestion['segment'],
): DataOriginQuestion['segment'] => {
  if (typeof segment === 'number') {
    return segment
  }

  return segment ? parseSegment(segment) : undefined
}

const parseDataOriginQuestion = (question: BackendDataOriginQuestion): DataOriginQuestion => ({
  questionTypeId: question.question_type_id,
  questionType: question.question_type,
  questionId: question?.question_id || undefined,
  questionName: question?.question_name || undefined,
  year: question.year || undefined,
  segment: parseOriginSegment(question?.segment),
})

const parseDataOrigin = (dataOrigin: BackendDataOrigin): DataOrigin => ({
  dataSource: dataOrigin?.data_source,
  dataSourceId: dataOrigin?.data_source_id || undefined,
  factId: dataOrigin?.fact_id || undefined,
  question: dataOrigin.question ? parseDataOriginQuestion(dataOrigin.question) : undefined,
})

export const parseCalculationTree = (
  calculationTree: BackendCalculationTree | SdiFormulaCalculationTree,
): CalculationTree => ({
  value: {
    value: calculationTree.value.value ?? undefined,
    unit: calculationTree.value.unit,
  },
  date: calculationTree.date
    ? {
        start: calculationTree.date.start || undefined,
        end: calculationTree.date.end,
      }
    : undefined,
  dataOrigin:
    typeof calculationTree?.data_origin === 'string' || !calculationTree.data_origin
      ? calculationTree.data_origin
      : parseDataOrigin(calculationTree.data_origin),
  operation: calculationTree?.operation || undefined,
  children: !!calculationTree?.children?.length
    ? calculationTree.children.map(parseCalculationTree)
    : undefined,
  commentary: ('commentary' in calculationTree && calculationTree.commentary) || undefined,
})

export const parseKpiResult = (kpiResult: BackendKpiResult): KpiResult => ({
  id: kpiResult.kpi_result_id,
  value: { value: kpiResult.value[0], unit: kpiResult.value[1] },
  kpiId: kpiResult.kpi_id,
  entityId: kpiResult.entity_id,
  start: kpiResult.start,
  end: kpiResult.end,
  year: kpiResult.year,
  calculationTree: kpiResult.calculation_tree
    ? parseCalculationTree(kpiResult.calculation_tree)
    : null,
})
